import {
  CalendarEvent,
  createScheduleModalContent,
  LoadActions,
  RegistrationCalendar,
  ScheduleListView,
  useIsDesktop,
} from "@timeedit/registration-components";
import { LoadRegistrationExpandBody } from "@timeedit/registration-schemas";
import { RegistrationError, translations } from "@timeedit/registration-shared";
import { Typography, Spin, usePageTitle } from "@timeedit/ui-components";
import { Layout } from "@timeedit/ui-components/antd";
import { useMemo } from "react";
import { ViewsProps } from "react-big-calendar";
import {
  useFetchRegistrationQuery,
  useLazyFetchExpandQuery,
} from "../../redux/api/registration";
import { useMapping, useOrganizationName } from "../../redux/hooks";
import {
  useAnalytics,
  useSchedulePageGrouping,
} from "../../utils/useAnalytics";
import { useIsHub } from "../RootPage/HubLayoutContext";
import "./schedule.scss";

const { Header, Content } = Layout;

export function Schedule(): React.JSX.Element {
  const isHub = useIsHub();
  const organizationName = useOrganizationName();
  usePageTitle(translations.schedule, { organizationName });
  const isDesktop = useIsDesktop();
  useSchedulePageGrouping();
  const views = createViews();
  const mapping = useMapping();
  const { analyticsEvent } = useAnalytics();
  const registration = useFetchRegistrationQuery();
  const [triggerExpandQuery] = useLazyFetchExpandQuery();
  const loadActions: LoadActions = useMemo(() => {
    return {
      loadRegistrationExpand: async (body: LoadRegistrationExpandBody) =>
        triggerExpandQuery(body, true).unwrap(),
    };
  }, [triggerExpandQuery]);

  if (registration.isError) {
    throw new RegistrationError({
      errors: [registration.error],
    });
  }

  if (registration.isLoading || !registration.data) {
    return <Spin center />;
  }

  const title = (
    <Typography.Title level={3} className={"schedule-page__header-title"}>
      {translations.schedule}
    </Typography.Title>
  );

  return (
    <Layout>
      {!isHub && <Header className={"schedule-page__header"}>{title}</Header>}
      <Content className={"schedule-page__content"}>
        <RegistrationCalendar
          mapping={mapping}
          registration={registration.data}
          translations={translations}
          views={views}
          title={title}
          eventModalContent={createScheduleModalContent({
            registration: registration.data,
            mapping,
            translations,
            loadActions,
          })}
          loadActions={loadActions}
          analyticsEvent={analyticsEvent}
          isDesktop={isDesktop}
        />
      </Content>
    </Layout>
  );

  function createViews(): ViewsProps<CalendarEvent> {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
      () => ({
        week: true,
        work_week: true,
        agenda: ScheduleListView.default,
      }),
      []
    );
  }
}
